<script setup>
/**
 * The written entry at the top of a day's page (journal redesign 2026-10-27):
 * a drop cap, italic ink, "Written overnight from your day · Edit". The first
 * time it is read it writes itself in, word by word, once. The intention's
 * "Did it hold?" is not on the Journal (Chris 2026-10-28): it lives on the Altar.
 */
import { computed } from 'vue'
import { plainEntry, splitWords } from '@/utils/journal-book'

const props = defineProps({
  entry: { type: Object, required: true },
  writing: { type: Boolean, default: false },      // the first read: write itself in
  ownWords: { type: Boolean, default: false },      // the day has the seeker's own lines (journal practice)
})
const emit = defineEmits(['edit'])

const STEP = 0.045
const hasTags = computed(() => /<[a-z][\s\S]*>/i.test(props.entry.text || ''))
const plain = computed(() => plainEntry(props.entry.text))
const words = computed(() => {
  let n = 0
  return splitWords(plain.value.slice(1)).map((w) => (/^\s+$/.test(w) ? { space: w } : { w, delay: (0.5 + (n++) * STEP).toFixed(2) + 's' }))
})
</script>

<template>
  <div class="entry" data-testid="journal-entry">
    <!-- Tapping the entry opens its editor in place (journal practice §7b): the one edit pattern. -->
    <div class="tap" role="button" tabindex="0" aria-label="Change the entry" data-testid="journal-entry-text" @click="emit('edit')" @keydown.enter.prevent="emit('edit')">
      <p v-if="writing" class="et writing"><span class="cap">{{ plain[0] }}</span><template v-for="(x, i) in words" :key="i"><template v-if="x.space">{{ x.space }}</template><span v-else class="w" :style="{ animationDelay: x.delay }">{{ x.w }}</span></template></p>
      <!-- eslint-disable-next-line vue/no-v-html -- the seeker's own entry, as the old Journal showed it -->
      <div v-else-if="hasTags" class="et et-html" v-html="entry.text"></div>
      <p v-else class="et"><span class="cap">{{ plain[0] }}</span>{{ plain.slice(1) }}<span class="pen" aria-hidden="true">✎</span></p>
    </div>
    <div class="emeta">
      <span>{{ entry.edited ? 'Edited by you' : ownWords ? 'Written overnight from your own words' : 'Written overnight from your day' }}</span>
      <button type="button" class="lnk" data-testid="journal-entry-edit" @click="emit('edit')">Edit</button>
    </div>
  </div>
</template>

<style scoped>
.entry { position: relative; margin: 2px 0 8px; padding: 2px 0 0; }
.et { margin: 0; font: italic 500 20px/32px var(--j-serif); color: var(--j-ink); text-wrap: pretty; }
.et .cap, .et-html :deep(p:first-child)::first-letter, .et-html.et::first-letter { float: left; font: 600 50px/40px var(--j-display); color: var(--j-hand); margin: 6px 8px 0 0; font-style: normal; }
.et-html :deep(p) { margin: 0 0 8px; }
.et-html :deep(.j-added) { display: block; margin: 8px 0; padding-left: 10px; border-left: 2px solid rgba(140, 102, 40, .4); }
.et-html :deep(.j-added-ref) { display: block; color: var(--j-ink2); font-size: 16px; }
.et .w { display: inline-block; white-space: pre; }
.tap { cursor: text; border-radius: 4px; transition: background .15s; -webkit-tap-highlight-color: transparent; }
.tap:focus-visible { background: rgba(201, 169, 78, .08); outline: 0; }
@media (hover: hover) { .tap:hover { background: rgba(201, 169, 78, .08); } }
.pen { display: inline-block; margin-left: 6px; font: normal 600 12px var(--j-ui); color: var(--j-hand); opacity: .55; vertical-align: 2px; }
.writing .w { opacity: 0; filter: blur(2px); transform: translateY(2px); animation: inkw .5s forwards; }
@keyframes inkw { to { opacity: 1; filter: none; transform: none; } }
.emeta { display: flex; align-items: center; gap: 12px; margin-top: 6px; font: 500 12.5px var(--j-ui); color: var(--j-ink3); }
.lnk { color: var(--j-ink2); font: 600 12.5px var(--j-ui); text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgba(88, 70, 111, .35); background: none; border: 0; padding: 4px 0; cursor: pointer; }
</style>
